import React from 'react';

import { SortableWrapper } from 'src/components/SortableWrapper';
import { DraggableCustomPropertyRow } from 'src/features/crm/properties/CustomPropertyRow';
import { useAppDispatch } from 'src/hooks/useStore';
import { usePatchCompanyMutation } from 'src/services/api/companiesApi';
import { updateClientUser } from 'src/store/clients/actions';
import { AddressCustomFieldValues } from 'src/store/clients/types';
import { isTemporaryCompanyId } from 'src/utils/CompanyUtils';

import {
  CustomFieldValueData,
  CustomPropertiesListProps,
} from '../clientDetailsTypes';

interface SortableCustomPropertiesListProps extends CustomPropertiesListProps {
  onSortEnd: (oldIndex: number, newIndex: number) => void;
}

export const SortableCustomPropertiesList: React.FC<
  SortableCustomPropertiesListProps
> = ({
  setPropertiesMenuAnchor,
  anchoredCustomFieldId,
  clientUser,
  company,
  customFields,
  openUpdateCustomFieldMenu,
  onSaveProperty,
  onSortEnd,
}) => {
  const dispatch = useAppDispatch();
  const [patchCompany] = usePatchCompanyMutation();
  const [activeFieldId, setActiveFieldId] = React.useState<string | null>(null);
  // Optimistic company details must not accept property edits until the real id lands.
  const isTemporaryCompany =
    !clientUser && !!company?.id && isTemporaryCompanyId(company.id);

  /**
   * This method get the custom field value
   * from client customFields by field's id.
   * @param customFieldId the id of the custom field
   */
  const getCustomFieldValue = (
    customFieldId: string,
  ): string | string[] | AddressCustomFieldValues | undefined => {
    if (clientUser?.fields?.customFields) {
      return clientUser.fields.customFields[customFieldId];
    } else if (company?.fields?.customFields) {
      return company.fields.customFields[customFieldId];
    }
    return undefined;
  };

  /**
   * This function updates client user data with
   * custom field value.
   * @param customFieldValue
   * @returns
   */
  const handleUpdateClientCustomFieldValue = async (
    customFieldData: CustomFieldValueData,
  ) => {
    if (!clientUser) {
      // Update company custom field value
      if (!company?.id || isTemporaryCompanyId(company.id)) {
        return;
      }

      const updatedCompanyCustomFields = {
        ...company.fields.customFields,
        [customFieldData.fieldId]: customFieldData.value || '',
      };

      await patchCompany({
        companyId: company.id,
        customFields: updatedCompanyCustomFields,
      }).unwrap();
      return;
    }
    const updatedClientCustomFields = {
      ...clientUser.fields.customFields,
      [customFieldData.fieldId]: customFieldData.value,
    };
    const updateOptions = {
      showSuccessMessage: false,
      isOptimisticChange: true,
    };
    dispatch(
      updateClientUser(
        {
          userId: clientUser.id,
          cognitoFirstName: clientUser.fields.givenName,
          customFields: updatedClientCustomFields,
          companyId: clientUser.fields.companyId,
          companyName: clientUser.fields.companyName,
          additionalFields: clientUser.additionalFields,
        },
        updateOptions,
      ),
    );
  };

  const sortableIds = (customFields || []).map((f) => f.id);

  return (
    <div
      className={
        isTemporaryCompany ? 'pointer-events-none opacity-50' : undefined
      }
      aria-disabled={isTemporaryCompany || undefined}
    >
      <SortableWrapper
        items={sortableIds}
        onSortEnd={isTemporaryCompany ? () => {} : onSortEnd}
        renderOverlay={(activeId) => {
          const customField = (customFields || []).find(
            (f) => f.id === activeId,
          );
          if (!customField) return null;
          return (
            <DraggableCustomPropertyRow
              onSaveProperty={onSaveProperty}
              id={customField.id}
              initialFieldValue={getCustomFieldValue(customField.id) || ''}
              field={{ ...customField }}
              onPropertyNameClicked={() => {}}
              onFieldValueUpdate={handleUpdateClientCustomFieldValue}
              setAsAnchor={false}
              setAnchorEl={setPropertiesMenuAnchor}
              isEditing={false}
              onSetActiveFieldId={setActiveFieldId}
            />
          );
        }}
      >
        <div className="flex flex-col">
          {customFields &&
            customFields.map((customField) => (
              <DraggableCustomPropertyRow
                onSaveProperty={onSaveProperty}
                key={customField.id}
                id={customField.id}
                initialFieldValue={getCustomFieldValue(customField.id) || ''}
                field={{
                  ...customField,
                }}
                onPropertyNameClicked={(
                  ev: React.MouseEvent<HTMLElement, MouseEvent>,
                ) => {
                  if (isTemporaryCompany) {
                    return;
                  }
                  openUpdateCustomFieldMenu(ev, customField);
                }}
                onFieldValueUpdate={handleUpdateClientCustomFieldValue}
                setAsAnchor={
                  !isTemporaryCompany &&
                  customField.id === anchoredCustomFieldId
                }
                setAnchorEl={setPropertiesMenuAnchor}
                isEditing={
                  !isTemporaryCompany && activeFieldId === customField.id
                }
                onSetActiveFieldId={
                  isTemporaryCompany ? () => null : setActiveFieldId
                }
              />
            ))}
        </div>
      </SortableWrapper>
    </div>
  );
};
